/* eslint-disable @typescript-eslint/restrict-template-expressions */
import CommodityApi from '@/api/commodity';
import type { OrdersV2 } from '@/api/ordersV2/types';
import { BaseAgreementData } from '@/components/agreements';
import LoanContractDialog from '@/components/loan-contract';
import ResultPage, { ResultPageStatusEnum } from '@/components/result-page';
import { ContractEnum } from '@/enums/contract-type';
import { OrderStatusV2Enum } from '@/enums/orderV2';
import { RouterV2Enum } from '@/router/router-names';
import store from '@/store';
import FormField from '@/components/form-field';
import {
  Component, Prop, Vue,
} from 'vue-property-decorator';
import { Toast } from '@hb/coffee';
import { openThirdPage } from '@/api/native/utils';
import ProductCommodity, {
  ALIPAYMINI_COMMODITY, NEW_YINHE_COMMODITY, YINHE_COMMODITY,
} from '@/shared/commodity';
import { openContract } from '@/ContractConfig';
import { getPostLendingContractFileType } from '@/shared/contract-controller';
import { jumpToNewVersion } from '@/shared/utils/jumpTo';
import { NewVersionEnum } from '@/enums/newVersionRoute';
import ContractPreviewApi from '@/api/payOff-Certificate';
import SendEmailDialog from '@/components/email-dialog/index.vue';
import { ContractScene } from '@/api/payOff-Certificate/enum';
import platform from '@/shared/utils/platform';
import ApplyContentDetails from '../apply-content';

const style = require('./index.module.scss') as Dictionary;

@Component({ name: 'ApplySuccessDetails' })
export default class ApplySuccessDetails extends Vue {
  @Prop()
  private orderDetail!: OrdersV2.OrderDetail;

  @Prop()
  private commodityDetail!: OrdersV2.CommodityDetail;

  @Prop()
  private isLentOrPayoff!: boolean;

  private loanContract: BaseAgreementData = new BaseAgreementData();

  private shouldShowContract = '';

  /** 显示弹窗 */
  private dialogShow = false;

  private get repayPlan() {
    return this.orderDetail && this.orderDetail.planTrail
      ? this.orderDetail.planTrail
      : [];
  }

  /** 显示dialog */
  private showSendEmailDialog() {
    this.dialogShow = true;
    this.$ubtModuleExpose('finance_loan_payoffCertificate_sendEmailPopup');
  }

  /** 关闭dialog */
  private closeSendEmailDialog() {
    this.dialogShow = false;
  }

  /**
  * 银河借款合同临时处理方案
  * 后续协议是 pdf 的，但是不支持跨域的，这里配置不需要调整，通过修改contractConfig配置来处理
  * getContractFileType
  */
  private get currentUsingNative() {
    return [
      YINHE_COMMODITY,
      NEW_YINHE_COMMODITY,
      ALIPAYMINI_COMMODITY,
      ProductCommodity.GUOMEI_NEW_COMMODITY,
      ProductCommodity.HAOFENQI_COMMODITY,
    ].indexOf(this.commodityDetail && `${this.commodityDetail.commodityId}`) >= 0;
  }

  private async created() {
    await this.getLoanContract();
  }

  private async getLoanContract() {
    // 当前渠道是否需要显示借款合同
    const SHOULD_SHOW_CONTRACT_CONFIG_NAME = 'billDisplayContract';
    if (this.commodityDetail && this.commodityDetail.commodityId) {
      this.shouldShowContract = (
        await CommodityApi.getCommodityConfig(
          String(this.commodityDetail.commodityId) || '0',
          SHOULD_SHOW_CONTRACT_CONFIG_NAME,
          this.commodityDetail.capitalCode,
        )
      )[SHOULD_SHOW_CONTRACT_CONFIG_NAME];
    }
  }

  // 借款合同展示
  private get isVisibleShouldContract() {
    return ['true'].indexOf(this.shouldShowContract) >= 0;
  }

  // 还款记录
  private onCheckPayRecord() {
    this.$ubtPageButton('finance_withDrawDetails_successful_repaymentRecord');
    this.$router.push({
      path: RouterV2Enum.repayRecords,
      query: {
        loanOrderId: this.orderDetail.loanOrderId,
        isCurrentOrderRecords: 'true',
      },
    });
  }

  /** 查看结清证明 */
  private async goPayOffCertificate() {
    /** 微信小程序不支持查看”结清证明“ */
    if (platform.isWechatMini) {
      jumpToNewVersion(NewVersionEnum.MINI_PROGRAMS_PROMPT, false);
      return;
    }

    const result = await ContractPreviewApi.getContractPreviewDetail(
      ContractScene.APPLY, this.orderDetail.loanOrderId,
    );
    // 如果有进行中的申请就展示已申请的提示
    if (result.pending === true) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      Toast('您的结清证明申请正在审核中，请稍后再试');
    } else {
      // 如果有预览链接就跳转到预览页
      if (result.url) {
        this.$ubtPageButton('finance_loan_payoffCertificate');
        jumpToNewVersion(NewVersionEnum.CONTRACT_PREVIEW, false, `loanOrderId=${this.orderDetail.loanOrderId}`);
      } else {
        // 没有预览链接就弹邮箱收集弹窗
        this.showSendEmailDialog();
      }
    }
  }

  // 查看借款合同
  private async showLoanContract() {
    // 借款合同需要的参数
    const contractParams = {
      orderId: this.orderDetail.loanOrderId,
      loanUsage: this.orderDetail.loanUsage,
      amount: this.orderDetail.amount,
      cardNo:
        (this.orderDetail.loanUserBankInfo
          && this.orderDetail.loanUserBankInfo.cardNo)
        || null,
    };

    const { commodityId } = this.commodityDetail;
    this.$ubtPageButtonNew('finance_withDrawDetails_successful_loanContract', {
      orderId: this.orderDetail.loanOrderId,
    });
    this.loanContract.init(
      Number(commodityId),
      ContractEnum.OFFICAL_LOAN_CONTRACT,
    );
    await this.loanContract.reloadNormal(contractParams, true);

    if (this.loanContract && this.loanContract.items.length === 1) {
      this.$ubtPageButtonNew('finance_loan_contract_view', {
        orderId: this.orderDetail.loanOrderId,
        contractName: this.loanContract.items[0].name,
      });
      if (this.currentUsingNative) {
        const { url } = this.loanContract.items[0];
        if (url) {
          openThirdPage(url);
        }
      } else {
        const loanContractItems = await getPostLendingContractFileType(
          String(this.commodityDetail.commodityId),
          this.loanContract.items,
        );
        openContract(loanContractItems[0]);
      }
    } else {
      if (this.loanContract && this.loanContract.items.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        Toast('合同正在生成中，请稍后再试');
      } else {
        this.$nextTick(async () => {
          const loanContractItem = await getPostLendingContractFileType(
            String(this.commodityDetail.commodityId),
            this.loanContract.items,
          );

          (this.$refs.loanContractDialog as LoanContractDialog).show(
            loanContractItem,
            this.orderDetail.loanOrderId,
          );
        });
      }
    }
  }

  // 去还款
  private onPay() {
    this.$ubtPageButton(
      'finance_withDrawDetails_successful_toRepay',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      'current', `${store.state.base.repayState.current}`,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      'overDue', `${store.state.base.repayState.overDue}`,
    );
    this.$router.push({
      path: RouterV2Enum.myBillAll,
      query: {
        loanOrderId: this.orderDetail?.loanOrderId || '',
        commodityId: String(this.commodityDetail?.commodityId || ''),
      },
    });
  }

  // 查看还款计划
  private goPayPlan() {
    this.$ubtPageButton('finance_withDrawDetails_successful_repaymentTrail');
    if (store.state.base.hitRepaymentExperiment) {
      this.$router.push({
        path: RouterV2Enum.myBillAll,
        query: {
          loanOrderId: this.orderDetail?.loanOrderId || '',
          commodityId: String(this.commodityDetail?.commodityId || ''),
        },
      });
    } else {
      this.$router.push(RouterV2Enum.myBill);
    }
  }

  private render() {
    return (
      <ResultPage
        title="提现成功"
        status={ResultPageStatusEnum.SUCCESS}
        productIcon={this.commodityDetail && this.commodityDetail.icon}
        amount={this.orderDetail.amount}
        confirmButtonText={
          this.orderDetail.state === OrderStatusV2Enum.PAYOFF ? '' : '去还款'
        }
        isLentOrPayoff={this.isLentOrPayoff}
        onConfirm={this.onPay.bind(this)}
      >
        <ApplyContentDetails
          orderDetail={this.orderDetail}
          commodityDetail={this.commodityDetail}
        />
        <div class={style.line}></div>

        {this.repayPlan.length > 0
          ? [
            <FormField name="还款计划" onClick={this.goPayPlan.bind(this)} />,
            <div class={[style.hairLines]}>
              <div class={'Hairline'}></div>
            </div>,
          ]
          : null}

        <FormField name="还款记录" onClick={this.onCheckPayRecord.bind(this)} />

        {this.isVisibleShouldContract && (
          <div class={[style.hairLines]}>
            <div class={'Hairline'}></div>
          </div>
        )}

        {this.isVisibleShouldContract && (
          <FormField name="借款合同" onClick={this.showLoanContract.bind(this)} />
        )}

        {this.orderDetail.payOffCertificate && (
          <div class={[style.hairLines]}>
            <div class={'Hairline'}></div>
          </div>
        )}

        {this.orderDetail.payOffCertificate && (
          <FormField name="结清证明" onClick={this.goPayOffCertificate.bind(this)} />
        )}

        <LoanContractDialog ref="loanContractDialog" />

        <SendEmailDialog
          show={this.dialogShow}
          loanOrderId={this.orderDetail.loanOrderId}
          onClose={this.closeSendEmailDialog.bind(this)}
        />
      </ResultPage>
    );
  }
}
